import { workspaceChangeTypesSelector, baselineChangeTypesSelector } from "../../changes/selector/changeTypeSelector";
import { workspaceModeSelector } from "./configurationSelector";
import { Layer, RootState } from "../../rootState";
import { isArray, isNil } from "lodash";
import { getCurrentResolution } from "../../../MapStore2/web/client/utils/MapUtils";
import { isInsideResolutionsLimits } from "../../../MapStore2/web/client/utils/LayersUtils";
import { defaultQueryableFilter } from "../../../MapStore2/web/client/utils/MapInfoUtils";

const BACKGROUND_LAYER_GROUP = "background";
const BASELINE_LAYER_GROUP = "Baseline";
const WORKSPACE_LAYER_GROUP = "Workspace";

const selectableLayersSelector = (state: RootState) =>
    queryableLayersSelector(state)
        .filter(
            (layer: Layer) =>
                layer.group.startsWith(BASELINE_LAYER_GROUP) ||
                (state.urlParams && state.urlParams.slots && state.urlParams.slots.haveSlotIds())
        )
        .filter((layer: Layer) => !layer.notSelectable);

const queriableWorspaceLayerSelector = (state: RootState) =>
    queryableLayersSelector(state).filter(
        (layer: Layer) =>
            (workspaceChangeTypesSelector(state) &&
                (workspaceChangeTypesSelector(state).length > 0 || isCesium(state)) &&
                layer.group.startsWith(BASELINE_LAYER_GROUP)) ||
            (workspaceModeSelector(state) && layer.group.startsWith(WORKSPACE_LAYER_GROUP))
    );
const queriableBaselineLayerSelector = (state: RootState) =>
    queryableLayersSelector(state).filter(
        (layer: Layer) =>
            ((baselineChangeTypesSelector(state) &&
                (baselineChangeTypesSelector(state).length > 0 || isCesium(state))) ||
                workspaceModeSelector(state)) &&
            layer.group.startsWith(BASELINE_LAYER_GROUP)
    );

const wfs3dWorkspaceLayerSelector = (state: RootState) =>
    layersSelector(state).filter(
        (layer: Layer) =>
            isCesium(state) &&
            layer.wfs3dLayer &&
            state.urlParams &&
            state.urlParams.slots &&
            state.urlParams.slots.haveSlotIds()
    );
const wfs3dBaselineLayerSelector = (state: RootState) =>
    layersSelector(state).filter((layer: Layer) => isCesium(state) && layer.wfs3dLayer);

const queriableWfs3dWorkspaceLayerSelector = (state: RootState) =>
    queryableLayersSelector(state).filter((layer: Layer) => layer.wfs3dLayer);
const queriableWfs3dBaselineLayerSelector = (state: RootState) =>
    queryableLayersSelector(state).filter((layer: Layer) => layer.wfs3dLayer);

const queriableWmsWorkspaceLayerSelector = (state: RootState) =>
    queriableWorspaceLayerSelector(state).filter((layer: Layer) => !isCesium(state) || !layer.wfs3dLayer);
const queriableWmsBaselineLayerSelector = (state: RootState) =>
    queriableBaselineLayerSelector(state).filter((layer: Layer) => !isCesium(state) || !layer.wfs3dLayer);

const wmsLayerSelector = (state: RootState) =>
    layersSelector(state).filter(
        (layer: Layer) => layer.group !== BACKGROUND_LAYER_GROUP && (!isCesium(state) || !layer.wfs3dLayer)
    );

const allLayersSelector = (state: RootState) =>
    layersSelector(state).filter((layer: Layer) => layer.group !== BACKGROUND_LAYER_GROUP);

const allWorkspaceLayersSelector = (state: RootState) =>
    allLayersSelector(state).filter(
        (layer: Layer) =>
            !workspaceModeSelector(state) || (layer.group && layer.group.startsWith(WORKSPACE_LAYER_GROUP))
    );
const allBaselineLayersSelector = (state: RootState) =>
    allLayersSelector(state).filter((layer: Layer) => layer.group && layer.group.startsWith(BASELINE_LAYER_GROUP));

const layersSelector = ({ layers }: RootState) => (layers && isArray(layers) ? layers : layers && layers.flat);

const queryableLayersSelector = (state: RootState) =>
    layersSelector(state).filter((layer) => isLayerQueryable(state, layer));

const visibleQueryableLayersSelector = (state: RootState) =>
    layersSelector(state).filter((layer) => defaultQueryableFilter(layer) && layer.visibility);

const isCesium = (state: RootState) => mapTypeSelector(state) === "cesium";

const mapTypeSelector = (state: RootState) => (state && state.maptype && state.maptype.mapType) || "leaflet";

const isLayerQueryable = (state: RootState, layer: Layer) => {
    const map = mapSelector(state);
    const currentResolution = isNil(map!.resolution)
        ? getCurrentResolution(Math.round(map!.zoom), 0, 21, 96)
        : map!.resolution;
    return isInsideResolutionsLimits(layer, currentResolution) && defaultQueryableFilter(layer);
};

const mapSelector = (state: RootState) => state.map!.present;

export {
    queriableWmsWorkspaceLayerSelector,
    queriableWmsBaselineLayerSelector,
    wfs3dWorkspaceLayerSelector,
    wfs3dBaselineLayerSelector,
    queriableWfs3dWorkspaceLayerSelector,
    queriableWfs3dBaselineLayerSelector,
    allWorkspaceLayersSelector,
    allBaselineLayersSelector,
    allLayersSelector,
    BASELINE_LAYER_GROUP,
    WORKSPACE_LAYER_GROUP,
    selectableLayersSelector,
    wmsLayerSelector,
    visibleQueryableLayersSelector,
    queryableLayersSelector,
    isLayerQueryable,
};
